import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Bookmark, Trash2, Ship, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum } from "@/lib/constants";

const STATUS_COLORS = {
  new: "bg-cyan/15 text-navy border-cyan/40",
  in_progress: "bg-amber-100 text-amber-700 border-amber-300",
  quoted: "bg-signal/10 text-signal border-signal/30",
  closed: "bg-secondary text-muted-foreground border-border",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = () => {
    api.get("/my/enquiries").then(({ data }) => setEnquiries(data));
    api.get("/my/saved-routes").then(({ data }) => setRoutes(data));
    api.get("/my/bookings").then(({ data }) => setBookings(data));
  };
  useEffect(() => { load(); }, []);

  const delRoute = async (id) => {
    await api.delete(`/my/saved-routes/${id}`);
    toast.success("Saved route removed");
    load();
  };

  return (
    <Layout>
      <div className="bg-navy text-white py-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <p className="text-signal font-mono-data text-xs">CUSTOMER DASHBOARD</p>
          <h1 className="font-heading font-extrabold text-3xl mt-1">Welcome, {user?.name?.split(" ")[0] || "Trader"}</h1>
          <p className="text-white/60 mt-1">{user?.email}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
        <Tabs defaultValue="bookings">
          <TabsList className="rounded-sm">
            <TabsTrigger value="bookings" data-testid="tab-bookings">My Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">My Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="routes" data-testid="tab-routes">Saved Routes ({routes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            {bookings.length === 0 ? (
              <Empty text="No bookings yet." action="Find a rate to book" onClick={() => navigate("/rates?shipment_type=export")} />
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="border border-border rounded-md p-5 bg-card" data-testid={`booking-item-${b.id}`}>
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        {b.booking_ref && <div className="font-mono-data text-xs font-bold text-signal mb-1" data-testid={`booking-ref-${b.id}`}>{b.booking_ref}</div>}
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-navy">{b.pol} → {b.pod}</span>
                          <Badge className={`rounded-sm border capitalize ${STATUS_COLORS[b.status] || STATUS_COLORS.new}`}>{(b.status || "new").replace("_", " ")}</Badge>
                        </div>
                        <div className="text-xs font-mono-data text-muted-foreground mt-1 capitalize">
                          {b.shipment_type} · {b.shipping_line} · {b.num_containers}× {b.container_size}
                        </div>
                      </div>
                      <div className="text-xs font-mono-data text-muted-foreground">{(b.created_at || "").slice(0, 10)}</div>
                    </div>
                    {b.rate_snapshot && (
                      <div className="mt-3 pt-3 border-t border-border text-sm font-mono-data text-navy">
                        {b.rate_snapshot.currency} {fmtNum(b.rate_snapshot.ocean_freight)} ocean freight + INR {fmtNum(b.rate_snapshot.local_charges_inr)} local · valid until {b.rate_snapshot.valid_until}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <Empty text="No enquiries yet." action="Search freight rates" onClick={() => navigate("/rates?shipment_type=export")} />
            ) : (
              <div className="space-y-3">
                {enquiries.map((e) => (
                  <div key={e.id} className="border border-border rounded-md p-5 bg-card" data-testid={`enq-item-${e.id}`}>
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-navy">{e.pol} → {e.pod}</span>
                          <Badge className={`rounded-sm border capitalize ${STATUS_COLORS[e.status] || STATUS_COLORS.new}`}>{(e.status || "new").replace("_", " ")}</Badge>
                        </div>
                        <div className="text-xs font-mono-data text-muted-foreground mt-1 capitalize">
                          {e.shipment_type} · {e.shipping_line || "Any line"} · {e.num_containers}× {e.container_size || e.container_type}
                        </div>
                      </div>
                      <div className="text-xs font-mono-data text-muted-foreground">{(e.created_at || "").slice(0, 10)}</div>
                    </div>
                    {e.selected_rate && (
                      <div className="mt-3 pt-3 border-t border-border text-sm font-mono-data text-navy">
                        {e.selected_rate.currency} {fmtNum(e.selected_rate.ocean_freight)} ocean freight · valid until {e.selected_rate.valid_until}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="routes" className="mt-6">
            {routes.length === 0 ? (
              <Empty text="No saved routes yet." action="Browse rates" onClick={() => navigate("/rates?shipment_type=export")} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {routes.map((r) => (
                  <div key={r.id} className="border border-border rounded-md p-5 bg-card" data-testid={`route-item-${r.id}`}>
                    <div className="flex justify-between items-start">
                      <Bookmark className="h-5 w-5 text-signal" />
                      <button onClick={() => delRoute(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="font-heading font-bold text-navy mt-3">{r.pol_name} → {r.pod_name}</div>
                    <div className="text-xs font-mono-data text-muted-foreground mt-1 capitalize">{r.shipment_type} · {r.container_size}</div>
                    <Button variant="ghost" className="px-0 mt-2 text-signal hover:bg-transparent" onClick={() => navigate(`/rates?shipment_type=${r.shipment_type}&pol_id=${r.pol_id}&pod_id=${r.pod_id}&container_type=${r.container_type}`)}>
                      Search this route <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const Empty = ({ text, action, onClick }) => (
  <div className="text-center py-16 border border-dashed border-border rounded-md">
    <Ship className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
    <p className="text-muted-foreground">{text}</p>
    <Button className="mt-4 bg-signal text-white rounded-sm" onClick={onClick}>{action}</Button>
  </div>
);
